import * as THREE from 'three';
import gsap from 'gsap';

class SocialVortex {
    constructor(containerId) {
        console.log("Initializing SocialVortex with container:", containerId);
        this.container = document.getElementById(containerId);
        this.scene = new THREE.Scene();
        this.camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
        this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        this.planes = [];
        this.count = 40;
        this.speed = 0.05;
        this.mouse = new THREE.Vector2();
        this.targetMouse = new THREE.Vector2();
        this.raycaster = new THREE.Raycaster();
        this.hoveredItem = null;
        this.isFrozen = false;

        this.init();
    }

    init() {
        this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        this.container.appendChild(this.renderer.domElement);

        this.camera.position.z = 5;

        // Lighting
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.8);
        this.scene.add(ambientLight);

        // Fog (Spring Theme Default)
        this.scene.fog = new THREE.FogExp2(0xf0f7f4, 0.02);

        this.createPlanes();
        this.addEventListeners();
        this.animate();
    }

    createPlanes() {
        const textureLoader = new THREE.TextureLoader();
        // Placeholder images from Unsplash
        const images = [
            'https://images.unsplash.com/photo-1516738901171-8eb4fc13bd20?w=400&q=80',
            'https://images.unsplash.com/photo-1543349689-9a4d426bee87?w=400&q=80',
            'https://images.unsplash.com/photo-1470770841072-f978cf4d019e?w=400&q=80',
            'https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=400&q=80'
        ];

        for (let i = 0; i < this.count; i++) {
            const geometry = new THREE.PlaneGeometry(1.5, 1);
            const material = new THREE.MeshBasicMaterial({
                map: textureLoader.load(images[i % images.length]),
                side: THREE.DoubleSide,
                transparent: true,
                opacity: 0.9
            });

            const mesh = new THREE.Mesh(geometry, material);
            mesh.userData.id = i;
            mesh.userData.text = i % 2 === 0 ? "#VisitFinland: Spring is here!" : "#Eurovision2024: Ready for the show!";
            this.resetPlane(mesh, i);
            this.scene.add(mesh);
            this.planes.push(mesh);
        }
    }

    resetPlane(mesh, index) {
        const radius = 4 + Math.random() * 8;
        const angle = (index / this.count) * Math.PI * 2;
        
        mesh.position.x = Math.cos(angle) * radius;
        mesh.position.y = Math.sin(angle) * radius;
        mesh.position.z = -100 - Math.random() * 100;
        
        mesh.rotation.z = 0; // Keep upright
        mesh.userData.angle = angle;
        mesh.userData.radius = radius;
    }

    updateTheme(theme) {
        if (theme === 'eurovision') {
            this.scene.fog.color.setHex(0x0d0221);
            this.speed = 0.15; // Faster for Eurovision
            // Add light streaks later if needed
        } else {
            this.scene.fog.color.setHex(0xf0f7f4);
            this.speed = 0.05;
        }
    }

    addEventListeners() {
        window.addEventListener('resize', () => {
            this.camera.aspect = window.innerWidth / window.innerHeight;
            this.camera.updateProjectionMatrix();
            this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
        });

        this.container.addEventListener('mousemove', (e) => {
            const rect = this.container.getBoundingClientRect();
            this.mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
            this.mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
        });

        this.container.addEventListener('click', () => {
            if (this.hoveredItem) {
                this.isFrozen = !this.isFrozen;
                if (this.isFrozen) {
                    this.selectedItem = this.hoveredItem;
                    gsap.to(this.selectedItem.scale, { x: 3, y: 3, duration: 0.8, ease: "power2.out" });
                    gsap.to(this.selectedItem.position, { x: 0, y: 0, z: 3, duration: 0.8, ease: "power2.out" });
                    this.showDetail(this.selectedItem.userData.text);
                } else {
                    this.hideDetail();
                    gsap.to(this.selectedItem.scale, { x: 1, y: 1, duration: 0.5 });
                    this.selectedItem = null;
                }
            }
        });
    }

    showDetail(text) {
        const overlay = document.getElementById('vortex-detail');
        overlay.querySelector('p').textContent = text;
        overlay.style.display = 'flex';
        gsap.fromTo(overlay, { opacity: 0 }, { opacity: 1, duration: 0.5 });
    }

    hideDetail() {
        const overlay = document.getElementById('vortex-detail');
        gsap.to(overlay, { opacity: 0, duration: 0.3, onComplete: () => overlay.style.display = 'none' });
    }

    animate() {
        requestAnimationFrame(this.animate.bind(this));

        if (!this.isFrozen) {
            this.planes.forEach((mesh, i) => {
                mesh.position.z += this.speed;
                
                // Vortex effect: spiral inwards slightly as they get closer
                mesh.userData.radius -= 0.005;
                mesh.position.x = Math.cos(mesh.userData.angle) * mesh.userData.radius;
                mesh.position.y = Math.sin(mesh.userData.angle) * mesh.userData.radius;

                if (mesh.position.z > 5) {
                    this.resetPlane(mesh, i);
                }
            });

            // Camera tilt control
            this.targetMouse.x += (this.mouse.x - this.targetMouse.x) * 0.05;
            this.targetMouse.y += (this.mouse.y - this.targetMouse.y) * 0.05;
            this.camera.rotation.y = -this.targetMouse.x * 0.1;
            this.camera.rotation.x = this.targetMouse.y * 0.1;
        }

        // Raycasting for hover
        this.raycaster.setFromCamera(this.mouse, this.camera);
        const intersects = this.raycaster.intersectObjects(this.planes);

        if (intersects.length > 0) {
            if (this.hoveredItem !== intersects[0].object) {
                this.hoveredItem = intersects[0].object;
                if (!this.isFrozen) {
                    gsap.to(this.hoveredItem.scale, { x: 1.2, y: 1.2, duration: 0.3 });
                }
            }
        } else {
            if (this.hoveredItem && !this.isFrozen) {
                gsap.to(this.hoveredItem.scale, { x: 1, y: 1, duration: 0.3 });
                this.hoveredItem = null;
            }
        }

        this.renderer.render(this.scene, this.camera);
    }
}

export default SocialVortex;
